import React, { useState, useEffect, useRef } from "react";
import {
  Upload,
  Type,
  ImageIcon,
  Shirt,
  Search,
  MessageCircle,
  Phone,
  ShoppingCart,
  Plus,
  Palette,
  RotateCw,
  Undo,
  Redo,
  X,
  Move,
  Loader2,
  Layers,
  Image,
  Text,
  ShapesIcon,
  DownloadIcon,
  BookTemplateIcon,
  CheckCircle2,
  CircleIcon,
  SquareIcon,
  TriangleIcon,
  Share2,
  ChevronDown,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import MainLayout from "@/components/layout/MainLayout";
import TShirtCanvas from "@/components/design/TShirtCanvas";
import DesignEditorPanel from "@/components/design/editor/DesignEditorPanel";
import TextEditor from "@/components/design/editor/TextEditor";
import NamesNumbersEditor from "@/components/design/editor/NamesNumbersEditor";
import SleeveDesignEditor from "@/components/design/editor/SleeveDesignEditor";
import { useIsMobile } from "@/hooks/use-mobile";
import { Link } from "react-router-dom";
import { constrainRotation } from "@/components/design/utils/designEditUtils";

const DesignLab = () => {
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Design state
  const [color, setColor] = useState("#ffffff");
  const [designUrl, setDesignUrl] = useState<string | null>(null);
  const [designPosition, setDesignPosition] = useState({ x: 50, y: 40 });
  const [viewMode, setViewMode] = useState<"front" | "back" | "sleeve">("front");
  const [rotation, setRotation] = useState(0);
  const [designSize, setDesignSize] = useState(0.8);
  const [productType, setProductType] = useState("T-shirt");
  const [productColor, setProductColor] = useState("White");
  
  // UI state
  const [openEditor, setOpenEditor] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const [showGrid, setShowGrid] = useState(false);
  const [showSleeveDialog, setShowSleeveDialog] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isAdjusting, setIsAdjusting] = useState(false);
  const [activeTab, setActiveTab] = useState("designs");
  const [showProductSelector, setShowProductSelector] = useState(false);

  // Listen for context menu design events
  useEffect(() => {
    const handleRotate = () => {
      setRotation(prev => constrainRotation(prev + 15));
    };

    const handleFlip = () => {
      setRotation(prev => constrainRotation(prev + 180));
    };

    const handleCenter = () => {
      setDesignPosition({ x: 50, y: 40 });
      toast({
        title: "Design centered",
        description: "Your design has been centered on the product.",
      });
    };

    document.addEventListener('design-rotate', handleRotate);
    document.addEventListener('design-flip', handleFlip);
    document.addEventListener('design-center', handleCenter);

    return () => {
      document.removeEventListener('design-rotate', handleRotate);
      document.removeEventListener('design-flip', handleFlip);
      document.removeEventListener('design-center', handleCenter);
    };
  }, [toast]);
  
  const handleDesignUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setIsUploading(true);
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setDesignUrl(reader.result as string);
        setOpenEditor("design");
        setIsAdjusting(true);
        setIsUploading(false);
        
        toast({
          title: "Design uploaded",
          description: `${file.name} has been uploaded successfully.`,
        });
      };

      reader.readAsDataURL(file);
    }
  };

  const handleViewChange = (view: "front" | "back" | "sleeve") => {
    setViewMode(view);
    if (view === "sleeve") {
      setShowSleeveDialog(true);
    }
  };

  const addToCart = () => {
    if (!designUrl) {
      toast({
        title: "No design added",
        description: "Please add elements to your design first before continuing.",
        variant: "destructive",
      });
      return;
    }
    
    toast({
      title: "Added to cart!",
      description: "Your custom design has been added to the cart.",
    });
  };

  const showComingSoon = () => {
    toast({
      title: "Coming Soon!",
      description: "This feature is not yet available but will be coming soon.",
    });
  };
  
  const handleRotationChange = (value: number) => {
    setRotation(constrainRotation(value));
  };
  
  const handleSaveDesign = () => {
    setOpenEditor(null);
    setIsAdjusting(false);
    
    toast({
      title: "Design saved",
      description: "Your design changes have been saved.",
    });
  };
  
  const handleAddText = (text: string) => {
    if (!text.trim()) {
      toast({
        title: "Text is empty",
        description: "Please enter some text to add to your design.",
        variant: "destructive",
      });
      return;
    }
    
    toast({
      title: "Text added",
      description: `"${text}" has been added to your design.`,
    });
  };
  
  const handleResetDesign = () => {
    setRotation(0);
    setDesignSize(0.8);
    setDesignPosition({ x: 50, y: 40 });
    
    toast({
      title: "Design reset",
      description: "Your design has been reset to the default position and size.",
    });
  };
  
  const handleDesignClick = () => {
    if (designUrl && !openEditor) {
      setOpenEditor("design");
      setIsAdjusting(true);
    }
  };
  
  const handleRemoveDesign = () => {
    setDesignUrl(null);
    setOpenEditor(null);
    setIsAdjusting(false);
    
    toast({
      title: "Design removed",
      description: "Your design has been removed.",
    });
  };
  
  const handleUploadButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Function to handle changing shirt color
  const changeShirtColor = (newColor: string, colorName: string) => {
    setColor(newColor);
    setProductColor(colorName);
    
    toast({
      title: "Color changed",
      description: `Product color changed to ${colorName}.`,
    });
  };

  // Function to handle position changes from the canvas component
  const handlePositionChange = (newPosition: { x: number, y: number }) => {
    setDesignPosition(newPosition);
  };

  // Function to change product type
  const changeProductType = (type: string) => {
    setProductType(type);
    setShowProductSelector(false);
    toast({
      title: "Product changed",
      description: `Changed to ${type}`,
    });
  };

  const productSelector = (
    <Dialog open={showProductSelector} onOpenChange={setShowProductSelector}>
      <DialogContent className="sm:max-w-[600px]">
        <h2 className="text-xl font-bold mb-4">Select a Product</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div 
            className="border rounded-md p-3 flex flex-col items-center hover:border-blue-500 cursor-pointer transition-colors"
            onClick={() => changeProductType("T-shirt")}
          >
            <Shirt className="h-16 w-16 mb-2" />
            <span>T-shirt</span>
          </div>
          <div 
            className="border rounded-md p-3 flex flex-col items-center hover:border-blue-500 cursor-pointer transition-colors"
            onClick={() => changeProductType("Hoodie")}
          >
            <Shirt className="h-16 w-16 mb-2" />
            <span>Hoodie</span>
          </div>
          <div 
            className="border rounded-md p-3 flex flex-col items-center hover:border-blue-500 cursor-pointer transition-colors"
            onClick={() => changeProductType("Tote Bag")}
          >
            <ShoppingCart className="h-16 w-16 mb-2" />
            <span>Tote Bag</span>
          </div>
          <div 
            className="border rounded-md p-3 flex flex-col items-center hover:border-blue-500 cursor-pointer transition-colors"
            onClick={() => changeProductType("Poster")}
          >
            <Image className="h-16 w-16 mb-2" />
            <span>Poster</span>
          </div>
          <div 
            className="border rounded-md p-3 flex flex-col items-center hover:border-blue-500 cursor-pointer transition-colors"
            onClick={() => changeProductType("Mug")}
          >
            <div className="h-16 w-16 mb-2 flex items-center justify-center">🍵</div>
            <span>Mug</span>
          </div>
          <div 
            className="border rounded-md p-3 flex flex-col items-center hover:border-blue-500 cursor-pointer transition-colors"
            onClick={() => changeProductType("Cap")}
          >
            <div className="h-16 w-16 mb-2 flex items-center justify-center">🧢</div>
            <span>Cap</span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );

  return (
    <div className="flex flex-col h-screen bg-white">
      {/* Header Bar */}
      <div className="border-b bg-white py-2 px-4 z-10">
        <div className="container mx-auto flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Link to="/">
              <Button variant="ghost" size="sm">
                <Shirt className="h-5 w-5 text-madts-purple mr-1" />
                <span className="font-semibold text-madts-purple">MAD-T's</span>
              </Button>
            </Link>
            <div className="h-5 border-r border-gray-300"></div>
            <Button 
              variant="outline" 
              size="sm" 
              className="text-sm font-medium"
              onClick={() => setShowProductSelector(true)}
            >
              {productType}
              <ChevronDown className="h-4 w-4 ml-1" />
            </Button>
          </div>
          
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={showComingSoon}>
              <Share2 className="h-4 w-4 mr-1" />
              Share
            </Button>
            <Button variant="outline" size="sm" onClick={showComingSoon}>
              <DownloadIcon className="h-4 w-4 mr-1" />
              Download
            </Button>
            <Button size="sm" className="bg-madts-purple hover:bg-madts-purple/90" onClick={addToCart}>
              Continue
              <CheckCircle2 className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex flex-grow overflow-hidden">
        {/* Left Sidebar */}
        <div className="w-64 border-r flex flex-col bg-gray-50">
          <Tabs defaultValue="elements" className="w-full" value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="w-full grid grid-cols-3 h-auto p-0 bg-gray-100">
              <TabsTrigger value="designs" className="py-3 data-[state=active]:bg-white">
                <Layers className="h-4 w-4 mr-2" />
                Templates
              </TabsTrigger>
              <TabsTrigger value="elements" className="py-3 data-[state=active]:bg-white">
                <ShapesIcon className="h-4 w-4 mr-2" />
                Elements
              </TabsTrigger>
              <TabsTrigger value="uploads" className="py-3 data-[state=active]:bg-white">
                <Upload className="h-4 w-4 mr-2" />
                Uploads
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="designs" className="m-0 p-4 overflow-auto h-[calc(100vh-10rem)]">
              <div className="mb-4">
                <Input type="text" placeholder="Search templates..." className="w-full" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                {Array.from({ length: 10 }).map((_, i) => (
                  <div key={i} className="border rounded-md bg-white overflow-hidden hover:border-blue-500 cursor-pointer">
                    <div className="h-28 bg-gray-200 flex items-center justify-center">
                      <BookTemplateIcon className="h-8 w-8 text-gray-400" />
                    </div>
                    <div className="p-2 text-xs">Template {i+1}</div>
                  </div>
                ))}
              </div>
            </TabsContent>
            
            <TabsContent value="elements" className="m-0 p-4 overflow-auto h-[calc(100vh-10rem)]">
              <div className="space-y-4">
                <div>
                  <h3 className="font-medium mb-2">Basic Shapes</h3>
                  <div className="grid grid-cols-4 gap-2">
                    <div className="border rounded-md p-2 flex items-center justify-center h-16 bg-white hover:border-blue-500 cursor-pointer">
                      <CircleIcon className="h-8 w-8 text-gray-700" />
                    </div>
                    <div className="border rounded-md p-2 flex items-center justify-center h-16 bg-white hover:border-blue-500 cursor-pointer">
                      <SquareIcon className="h-8 w-8 text-gray-700" />
                    </div>
                    <div className="border rounded-md p-2 flex items-center justify-center h-16 bg-white hover:border-blue-500 cursor-pointer">
                      <TriangleIcon className="h-8 w-8 text-gray-700" />
                    </div>
                    <div className="border rounded-md p-2 flex items-center justify-center h-16 bg-white hover:border-blue-500 cursor-pointer">
                      <Plus className="h-8 w-8 text-gray-700" />
                    </div>
                  </div>
                </div>
                
                <div>
                  <h3 className="font-medium mb-2">Text</h3>
                  <div className="space-y-2">
                    <div 
                      className="border rounded-md p-3 flex items-center bg-white hover:border-blue-500 cursor-pointer"
                      onClick={() => setOpenEditor("text")}
                    >
                      <Text className="h-5 w-5 mr-2 text-gray-700" />
                      <span>Add Text</span>
                    </div>
                    <div className="border rounded-md p-3 flex items-center bg-white hover:border-blue-500 cursor-pointer">
                      <div className="mr-2 text-xl font-bold">T</div>
                      <span>Add Heading</span>
                    </div>
                    <div className="border rounded-md p-3 flex items-center bg-white hover:border-blue-500 cursor-pointer">
                      <div className="mr-2 italic text-lg">T</div>
                      <span>Add Subheading</span>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h3 className="font-medium mb-2">Icons & Graphics</h3>
                  <div className="grid grid-cols-3 gap-2">
                    {Array.from({ length: 9 }).map((_, i) => (
                      <div key={i} className="border rounded-md p-2 flex items-center justify-center h-16 bg-white hover:border-blue-500 cursor-pointer">
                        <ImageIcon className="h-6 w-6 text-gray-400" />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="uploads" className="m-0 p-4 overflow-auto h-[calc(100vh-10rem)]">
              <div 
                className="border-2 border-dashed rounded-md p-8 mb-4 flex flex-col items-center justify-center text-center bg-white hover:border-blue-500 cursor-pointer"
                onClick={handleUploadButtonClick}
              >
                <Upload className="h-8 w-8 mb-2 text-gray-500" />
                <p className="text-sm mb-1">Upload your designs</p>
                <p className="text-xs text-gray-500">PNG, JPG, SVG up to 5MB</p>
                <Input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  ref={fileInputRef}
                  onChange={handleDesignUpload}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-2">
                {designUrl && (
                  <div className="border rounded-md overflow-hidden bg-white">
                    <div className="h-28 bg-gray-100 flex items-center justify-center p-2">
                      <img src={designUrl} alt="Uploaded design" className="max-h-full max-w-full object-contain" />
                    </div>
                    <div className="p-2 flex justify-between items-center">
                      <span className="text-xs truncate">Your design</span>
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={handleRemoveDesign}>
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
          
          <div className="mt-auto border-t p-3">
            <Button 
              variant="outline" 
              className="w-full"
              onClick={() => changeProductType(productType === "T-shirt" ? "Hoodie" : "T-shirt")}
            >
              <Shirt className="h-4 w-4 mr-2" />
              Change Product
            </Button>
          </div>
        </div>

        {/* Center Content */}
        <div className="flex-1 flex flex-col bg-gray-100 relative">
          {/* Editor Panel */}
          {openEditor === "design" && (
            <div className="absolute top-0 left-0 right-0 z-10 bg-white shadow-md p-4">
              <DesignEditorPanel
                onClose={() => {
                  setOpenEditor(null);
                  setIsAdjusting(false);
                }}
                designSize={designSize}
                setDesignSize={setDesignSize}
                rotation={rotation}
                setRotation={handleRotationChange}
                designPosition={designPosition}
                setDesignPosition={setDesignPosition}
                onSaveDesign={handleSaveDesign}
                onResetDesign={handleResetDesign}
                onRemoveDesign={handleRemoveDesign}
              />
            </div>
          )}
          
          {openEditor === "text" && (
            <div className="absolute top-0 left-0 right-0 z-10 bg-white shadow-md p-4">
              <TextEditor
                onClose={() => setOpenEditor(null)}
                onAddText={handleAddText}
              />
            </div>
          )}
          
          {openEditor === "names" && (
            <div className="absolute top-0 left-0 right-0 z-10 bg-white shadow-md p-4">
              <NamesNumbersEditor
                onClose={() => setOpenEditor(null)}
              />
            </div>
          )}

          {/* Design Canvas */}
          <div 
            className="flex-1 flex justify-center items-center overflow-hidden relative p-6"
          >
            {isUploading && (
              <div className="absolute inset-0 flex items-center justify-center bg-white/70 z-20">
                <div className="flex flex-col items-center">
                  <Loader2 className="h-10 w-10 animate-spin text-blue-600 mb-2" />
                  <p className="text-blue-600 font-medium">Processing your design...</p>
                </div>
              </div>
            )}

            <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-2xl h-full max-h-[600px] flex items-center justify-center">
              <TShirtCanvas
                color={color}
                designUrl={designUrl}
                rotation={false}
                designSize={designSize}
                showBackDesign={viewMode === "back"}
                designPosition={designPosition}
                designRotation={rotation}
                isAdjusting={isAdjusting}
                onDesignClick={handleDesignClick}
                onDesignRemove={handleRemoveDesign}
                onPositionChange={handlePositionChange}
              />
            </div>
          </div>
        </div>

        {/* Right Sidebar */}
        <div className="w-64 border-l flex flex-col bg-gray-50">
          <div className="p-4 border-b">
            <h3 className="font-medium mb-3">Product Options</h3>
            <div className="space-y-4">
              <div>
                <label className="text-sm text-gray-600 mb-1 block">Product Type</label>
                <Button 
                  variant="outline" 
                  className="w-full justify-between"
                  onClick={() => setShowProductSelector(true)}
                >
                  <span>{productType}</span>
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </div>
              
              <div>
                <label className="text-sm text-gray-600 mb-1 block">Product Color</label>
                <div className="grid grid-cols-6 gap-2">
                  {[
                    { hex: "#ffffff", name: "White" },
                    { hex: "#000000", name: "Black" },
                    { hex: "#ff0000", name: "Red" },
                    { hex: "#0000ff", name: "Blue" },
                    { hex: "#00ff00", name: "Green" },
                    { hex: "#ffff00", name: "Yellow" },
                  ].map((colorOption) => (
                    <div
                      key={colorOption.name}
                      className={`w-8 h-8 rounded-full cursor-pointer border hover:scale-110 transition-transform ${
                        color === colorOption.hex ? "ring-2 ring-offset-2 ring-blue-500" : ""
                      }`}
                      style={{ backgroundColor: colorOption.hex }}
                      onClick={() => changeShirtColor(colorOption.hex, colorOption.name)}
                      title={colorOption.name}
                    />
                  ))}
                </div>
              </div>
              
              <div>
                <label className="text-sm text-gray-600 mb-1 block">View</label>
                <div className="flex gap-2">
                  <Button 
                    variant={viewMode === "front" ? "default" : "outline"} 
                    size="sm"
                    className={viewMode === "front" ? "bg-madts-purple hover:bg-madts-purple/90" : ""}
                    onClick={() => handleViewChange("front")}
                  >
                    Front
                  </Button>
                  <Button 
                    variant={viewMode === "back" ? "default" : "outline"} 
                    size="sm"
                    className={viewMode === "back" ? "bg-madts-purple hover:bg-madts-purple/90" : ""}
                    onClick={() => handleViewChange("back")}
                  >
                    Back
                  </Button>
                </div>
              </div>
            </div>
          </div>
          
          <div className="p-4 border-b">
            <h3 className="font-medium mb-3">Design Settings</h3>
            {designUrl ? (
              <div className="space-y-3">
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={handleDesignClick}
                >
                  <Layers className="h-4 w-4 mr-2" />
                  Edit Design
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start" 
                  onClick={handleResetDesign}
                >
                  <RotateCw className="h-4 w-4 mr-2" />
                  Reset Position
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start text-red-500" 
                  onClick={handleRemoveDesign}
                >
                  <X className="h-4 w-4 mr-2" />
                  Remove Design
                </Button>
              </div>
            ) : (
              <div className="text-center py-4 text-gray-500">
                <p className="text-sm">No design elements yet</p>
                <p className="text-xs mt-1">Add elements from the sidebar</p>
              </div>
            )}
          </div>
          
          <div className="mt-auto p-4">
            <Button 
              className="w-full bg-madts-purple hover:bg-madts-purple/90" 
              onClick={addToCart}
            >
              Continue to Checkout
            </Button>
          </div>
        </div>
      </div>

      {/* Product selector dialog */}
      {productSelector}
      
      {/* Sleeve design dialog */}
      <Dialog open={showSleeveDialog} onOpenChange={setShowSleeveDialog}>
        <DialogContent className="max-w-4xl p-0">
          <SleeveDesignEditor onClose={() => setShowSleeveDialog(false)} />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DesignLab;
